// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { normalizeArray } from '../../../api/client';
import {
  listTechnicians,
  listTechAvailability,
  createTechAvailability,
  deleteTechAvailability,
} from '../../../api/schedulingApi';
import { getActiveTenant } from '../../../helpers/tenantHelpers';
import { getTeamVocab, cap } from '../../../constants/uiRegistry';
import { FormRow, Section, SectionRail } from './primitives';

/* Team — your people, invitations, and field technicians with their
   availability (moved here from Scheduling). Invites and availability are
   object actions; there are no page-level fields, so no save bar. */

const TEAM_LIST_ENDPOINTS = ['/accounts/team/', '/accounts/users/', '/tenant/users/'];
// The label is what the owner reads; the value is what accounts.capabilities
// .normalize_role recognises. 'Member (Tech)' sent verbatim normalised to SALES.
const ROLES = [
  { label: 'Member (Tech)', value: 'Technician' },
  { label: 'Manager', value: 'Manager' },
  { label: 'Admin', value: 'Admin' },
];

export default function TeamTab() {
  const tenantId = getActiveTenant()?.id || null;
  // Industry vocabulary: events -> "coordinators", landscaping -> "crew",
  // pest -> "technicians". Keeps the stable section id 'technicians' (used as the
  // scroll anchor) while the label the tenant reads is industry-correct.
  const vocab = getTeamVocab(getActiveTenant()?.industry);
  const SECTIONS = [
    { id: 'people', label: 'People' },
    { id: 'invite', label: 'Invite' },
    { id: 'technicians', label: cap(vocab.members) },
  ];
  const [members, setMembers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [invite, setInvite] = useState({ email: '', role: ROLES[0].value });
  const [inviting, setInviting] = useState(false);
  const [availDraft, setAvailDraft] = useState({ tech: '', start_at: '', end_at: '' });

  const load = useCallback(async () => {
    for (const endpoint of TEAM_LIST_ENDPOINTS) {
      try {
        const res = await api.get(endpoint);
        setMembers(normalizeArray(res.data));
        break;
      } catch { /* try the next shape */ }
    }
    try {
      const [t, a] = await Promise.all([listTechnicians(), listTechAvailability()]);
      setTechs(normalizeArray(t.data));
      setAvailability(normalizeArray(a.data));
    } catch { /* technicians are optional for non-field tenants */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!invite.email.trim() || !invite.email.includes('@')) {
      toast.error('Enter an email address');
      return;
    }
    setInviting(true);
    try {
      const { data } = await api.post('/accounts/auth/invite-user/', {
        email: invite.email.trim(), role: invite.role,
      });
      // 201 means the invite row exists, not that the email went out.
      if (data?.email_sent === false) {
        toast.error(data?.detail || 'Invite created, but the email could not be sent');
      } else {
        toast.success(data?.detail || `Invite sent to ${invite.email.trim()}`);
      }
      setInvite({ email: '', role: ROLES[0].value });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the invite');
    } finally {
      setInviting(false);
    }
  };

  const addAvailability = async () => {
    if (!availDraft.tech || !availDraft.start_at || !availDraft.end_at) {
      toast.error(`Pick a ${vocab.member}, start and end`);
      return;
    }
    try {
      // TechAvailability.kind is required (available | unavailable); this
      // form only ever adds a window the person CAN work, so it is 'available'.
      const { data } = await createTechAvailability({ ...availDraft, kind: 'available', tenant: tenantId });
      setAvailability((prev) => [...prev, data]);
      setAvailDraft({ tech: '', start_at: '', end_at: '' });
      toast.success('Availability added');
    } catch (e) {
      const body = e?.response?.data;
      const first = body && typeof body === 'object' ? Object.values(body).flat()[0] : null;
      toast.error(first ? `Could not add availability: ${first}` : 'Could not add availability');
    }
  };

  const removeAvailability = async (row) => {
    try {
      await deleteTechAvailability(row.id);
      setAvailability((prev) => prev.filter((a) => a.id !== row.id));
    } catch {
      toast.error('Could not remove it');
    }
  };

  const techName = (idOrObj) => {
    const tid = typeof idOrObj === 'object' ? idOrObj?.id : idOrObj;
    const t = techs.find((row) => String(row.id) === String(tid));
    return t?.name || t?.full_name || t?.first_name || `${cap(vocab.member)} ${tid}`;
  };

  const fmtWhen = (value) => {
    try { return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return value; }
  };

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} />
      <div className="sv2-col">
        <Section id="people" title="People"
          sub="Everyone with access to this workspace.">
          <div className="sv2-obj-list">
            {members.length === 0 && (
              <p className="sv2-section-sub">Loading your team…</p>
            )}
            {members.map((m) => (
              <div className="sv2-obj" key={m.id || m.email}>
                <div className="sv2-obj-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="sv2-obj-title">
                      {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.name || m.email}
                    </div>
                    <div className="sv2-obj-sub">{m.email}</div>
                  </div>
                  <span className="sv2-pill info">{m.role || 'Member'}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="invite" title="Invite someone"
          sub="They get an email link and set up their own account — no passwords to share.">
          <FormRow label="Email" help="Work email of the person you're inviting.">
            <input type="email" value={invite.email} placeholder="name@company.com"
              onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))} />
          </FormRow>
          <FormRow label="Role" help="What they can see and do. You can change it later.">
            <select value={invite.role}
              onChange={(e) => setInvite((p) => ({ ...p, role: e.target.value }))}>
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </FormRow>
          <div className="sv2-actions">
            <button className="sv2-btn primary" onClick={sendInvite} disabled={inviting}>
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </Section>

        <Section id="technicians" title={cap(vocab.members)}
          sub={`Your field team and when they're available for ${vocab.work}s — moved here from Scheduling.`}>
          <div className="sv2-obj-list">
            {techs.length === 0 && (
              <p className="sv2-section-sub">
                No {vocab.members} yet — invite someone with the Member (Tech) role and they'll appear here.
              </p>
            )}
            {techs.map((t) => {
              const slots = availability.filter((a) => String(a.tech?.id ?? a.tech) === String(t.id));
              return (
                <div className="sv2-obj" key={t.id}>
                  <div className="sv2-obj-head">
                    <div style={{ minWidth: 0 }}>
                      <div className="sv2-obj-title">{t.name || t.full_name || techName(t.id)}</div>
                      <div className="sv2-obj-sub">
                        {slots.length ? `${slots.length} availability window${slots.length === 1 ? '' : 's'}` : 'No availability set'}
                      </div>
                    </div>
                  </div>
                  {slots.map((s) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12, color: 'var(--text-2)' }}>
                      <span>{fmtWhen(s.start_at)} → {fmtWhen(s.end_at)}</span>
                      <button className="sv2-btn sm danger" onClick={() => removeAvailability(s)}>Remove</button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {techs.length > 0 && (
            <FormRow label="Add availability" help={`A window when a ${vocab.member} can take ${vocab.work}s.`}>
              <div style={{ display: 'grid', gap: 6 }}>
                <select value={availDraft.tech}
                  onChange={(e) => setAvailDraft((p) => ({ ...p, tech: e.target.value }))}>
                  <option value="">Pick a {vocab.member}</option>
                  {techs.map((t) => (
                    <option key={t.id} value={t.id}>{t.name || t.full_name || techName(t.id)}</option>
                  ))}
                </select>
                <input type="datetime-local" value={availDraft.start_at}
                  onChange={(e) => setAvailDraft((p) => ({ ...p, start_at: e.target.value }))} />
                <input type="datetime-local" value={availDraft.end_at}
                  onChange={(e) => setAvailDraft((p) => ({ ...p, end_at: e.target.value }))} />
                <button className="sv2-btn" onClick={addAvailability}>Add</button>
              </div>
            </FormRow>
          )}
        </Section>
      </div>
    </div>
  );
}
